<template>
  <cs-preloader-overlay :loading="loading">
    <div :class="[$style.container, { [$style.invalid]: isInvalidCode }]">
      <div :class="$style.header">
        <payment-header
          :title="$t('confirmation.sms.title')"
          :language="getLanguage"
        />
      </div>

      <payment-template>
        <div slot="content" :class="$style.content">
          <div :class="$style.image">
            <div :class="$style.device">
              <device-image />
            </div>
            <div :class="[$style.bubble, { [$style.invalid]: isInvalidCode }]">
              <speech-bubble-image />
            </div>
          </div>
          <div :class="$style.title">
            <h1>{{ $t('confirmation.sms.form.title') }}</h1>
          </div>
          <div :class="$style.form">
            <labeled-input
              v-model="smsCode"
              :label="displayLabel"
              :maxlength="8"
              :error="isInvalidCode"
              :force-replace="[/[^0-9]+/g, '']"
              type="tel"
              placeholder="*****"
              @focus="handleCodeFocus"
              @blur="$v.smsCode.$touch"
            />
          </div>
          <div :class="$style.submit">
            <simple-button
              variant="primary"
              :disabled="isInvalidForm"
              @click="handleSubmit"
            >
              {{ $t('confirmation.sms.form.submit') }}
            </simple-button>
          </div>
        </div>

        <base-footer slot="footer" security />
      </payment-template>
    </div>
  </cs-preloader-overlay>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import { required, minLength } from 'vuelidate/lib/validators';
import { getErrorMessage } from 'services/helpers/errorHelpers';

import HTTPService from '@/services/Http';

import DeviceImage from 'atoms/icons/custom/Device';
import SpeechBubbleImage from 'atoms/icons/custom/SpeechBubble';
import BaseFooter from 'molecules/base/BaseFooter';
import SimpleButton from 'molecules/common/SimpleButton';
import LabeledInput from 'molecules/common/LabeledInput';
import PaymentHeader from 'organisms/payment/PaymentHeader';
import PaymentTemplate from 'templates/PaymentTemplate';

const CODE_MIN_LENGTH = 3;

export default {
  name: 'SmsConfirmationCode',
  components: {
    DeviceImage,
    SpeechBubbleImage,
    BaseFooter,
    SimpleButton,
    LabeledInput,
    PaymentHeader,
    PaymentTemplate,
  },
  data() {
    return {
      smsCode: '',
      smsCodeStatus: null,
      loading: false,
    };
  },
  validations: {
    smsCode: {
      required,
      minLength: minLength(CODE_MIN_LENGTH),
      sms: function() {
        return this.smsCodeStatus !== 'BAD_OTP';
      },
    },
  },
  computed: {
    isInvalidCode() {
      return this.$v.smsCode.$error;
    },
    isInvalidForm() {
      return this.$v.smsCode.$invalid;
    },
    displayLabel() {
      const defaultLabel = this.$t('confirmation.sms.input.label');

      if (!this.isInvalidCode) {
        return defaultLabel;
      }

      const errorMessage = getErrorMessage(this.$v.smsCode);

      if (this.$validate.isString(errorMessage)) {
        return this.$t(errorMessage, {
          minLength: CODE_MIN_LENGTH,
        });
      }

      return defaultLabel;
    },
    ...mapState(['TRANSACTION']),
    ...mapGetters(['getLanguage']),
  },
  methods: {
    handleCodeFocus() {
      this.smsCodeStatus = null;
      this.$v.smsCode.$reset();
    },
    async handleSubmit() {
      if (this.isInvalidForm) {
        this.$alert({
          type: 'danger',
          title: 'mistakes.in.form',
        });
        return;
      }

      this.loading = true;

      try {
        const { data } = await HTTPService.confirmOneTimePassword({
          paymentId: this.TRANSACTION.id,
          otp: this.smsCode,
        });

        // статусы SUCCESS / FAIL / BAD_OTP
        if (data.status === 'SUCCESS') {
          this.$router.push({ name: 'payment.origin' });
          return;
        }

        if (data.status === 'BAD_OTP') {
          this.loading = false;

          this.smsCodeStatus = data.status;
          this.$v.smsCode.$touch();
          return;
        }

        this.$router.push({ name: 'failed.payment' });
      } catch (error) {
        console.error(error);
        // @TODO: вынесли обработку ошибок в сервис

        const fraudDeclined = error.data && error.data.status && error.data.status === 'FRAUD_DECLINE';

        if (error.status === 400 && error.data.status === 'TRANSACTION_LIMIT_EXCEEDED') {
          this.loading = false;

          this.$alert({
            type: 'danger',
            title: 'error.transition.card',
          });
          return;
        }

        if (error.status === 400 && fraudDeclined) {
          this.$router.push({ name: 'rejected.payment' });
          return;
        }

        if (error.status === 400) {
          this.$router.push({ name: 'failed.payment' });
          return;
        }

        this.$router.push({ name: 'service.error' });
      }
    },
  },
};
</script>

<style lang="sass" module>
  .container
    min-height: 100vh
    position: relative

    &:after
      content: ''
      position: absolute
      top: 0
      width: 100%
      height: 305px
      background: linear-gradient(249.76deg, rgba(36, 168, 77, 0.3) -8.32%, rgba(6, 138, 204, 0.3) 108.85%)
      z-index: -1

      +respond-to('medium')
        height: 365px

    &.invalid
      &:after
        background: rgba(255, 81, 114, 0.25)

  .header
    +respond-to('medium')
      margin-bottom: 30px

  .content
    +flexbox
    +flex-direction(column)
    +align-items(center)

  .image
    +size(auto, 100px)
    +flexbox
    position: relative
    margin-bottom: 30px

    +respond-to('medium')
      +size(auto, 130px)

    svg
      height: 100%

  .bubble
    position: absolute
    top: 22px
    right: 22px

    svg
      +size(50px, 40px)

    +respond-to('medium')
      top: 30px
      right: 10px

      svg
        +size(64px, 50px)

    &.invalid
      svg
        path:first-child
          fill: #FF5172

  .title
    margin-bottom: 30px
    text-align: center

    +respond-to('medium')
      margin-bottom: 20px

    h1
      +font-type('regular', 18, 22)
      color: var(--color-black-2)

  .form
    width: 100%
    margin-bottom: 20px

  .submit
    width: 100%
    margin-bottom: 20px
</style>
